import { type ChangeEvent, useCallback } from 'react'

import {
  actionsSelector,
  useStore,
  visitorPrivateActionsSelector,
} from '@liveHelper/models/store'
import {
  createUploadSignature,
  uploadFile,
} from '@oneToOne/shared/services/rest/apiService'
import { getBase64Image } from '@oneToOne/shared/utils/file'

import { logError } from '@core/logging'
import { getGuestId } from '@core/tracking/guestId'

/**
 * A hook to handle File change event
 */
export const useFileUpload = ():
  ((e: ChangeEvent<HTMLInputElement>) => Promise<void>) | undefined => {
  const { createMediaMessage } = useStore(actionsSelector)
  const { sendMediaMessage, updateMediaMessage } = useStore(
    visitorPrivateActionsSelector,
  )

  const guestId = getGuestId()

  const handleFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      if (!(
        e.target.files &&
        e.target.files.length > 0 &&
        sendMediaMessage &&
        updateMediaMessage
      )) {
        return
      }

      const file: File = e.target.files[0]
      const uploadSignature = await createUploadSignature({
        payload: {
          filename: file.name,
          mime_type: file.type,
          actor_id: guestId,
        },
      })
      const message = createMediaMessage(
        {
          filename: file.name,
          file_url: file.type.startsWith('image')
            ? await getBase64Image(file)
            : undefined,
          file_mime_type: file.type,
          file_size: file.size,
          file_key: uploadSignature.key,
        },
        'visitor',
      )

      try {
        await uploadFile({
          file,
          uploadSignature,
          onUploadProgress: (progressEvent) => {
            if (!message || !progressEvent.progress) {
              return
            }
            if (progressEvent.progress < 1) {
              updateMediaMessage(message, 'uploading', progressEvent.progress)
            } else {
              updateMediaMessage(message, 'success', 1)
            }
          },
        })
        sendMediaMessage(message)
      } catch (e) {
        logError(e)
        updateMediaMessage(message, 'error')
      }
    },
    [createMediaMessage, guestId, sendMediaMessage, updateMediaMessage],
  )

  return handleFileChange
}
